import { useState, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { ContactAttempt, Lead } from '../types/lead.types';
import { updateLead } from '../services/lead.service';
import { useAuth } from '../contexts/AuthContext';

export type ContactMethod = 'phone' | 'email' | 'text' | 'in-person';

export interface UseContactLoggerOptions {
  // Called after a successful save with the new attempts array. Used by
  // callers that hold lead state locally (e.g. LeadDetailModal).
  onSaved?: (updatedAttempts: ContactAttempt[]) => void;
}

// Shared form state + save mutation for logging a contact attempt against a
// lead. Used by both the inline expandable row in the spreadsheet list view
// and the modal's Contacts tab so the two stay in sync.
export const useContactLogger = (
  lead: Pick<Lead, 'id' | 'contactAttempts'>,
  options: UseContactLoggerOptions = {}
) => {
  const { currentUser, userProfile } = useAuth();

  const [method, setMethod] = useState<ContactMethod>('phone');
  const [notes, setNotes] = useState('');
  const [successful, setSuccessful] = useState(true);
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    setMethod('phone');
    setNotes('');
    setSuccessful(true);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!currentUser) {
      toast.error('You must be signed in to log a contact');
      return false;
    }
    if (!notes.trim()) {
      toast.error('Please add notes for this contact attempt');
      return false;
    }

    setSaving(true);
    try {
      const newAttempt: ContactAttempt = {
        timestamp: Timestamp.now(),
        method,
        notes: notes.trim(),
        userId: currentUser.uid,
        userName: userProfile?.name || 'Unknown User',
        successful,
      };

      const existing = lead.contactAttempts || [];
      const updatedAttempts = [...existing, newAttempt];

      await updateLead(lead.id, {
        contactAttempts: updatedAttempts,
        lastContacted: Timestamp.now(),
        lastUpdated: Timestamp.now(),
      });

      options.onSaved?.(updatedAttempts);
      toast.success('Contact logged');
      reset();
      return true;
    } catch (err) {
      console.error('Error logging contact:', err);
      toast.error('Failed to log contact');
      return false;
    } finally {
      setSaving(false);
    }
  }, [currentUser, userProfile, notes, method, successful, lead, options, reset]);

  return {
    method,
    setMethod,
    notes,
    setNotes,
    successful,
    setSuccessful,
    saving,
    save,
    reset,
  };
};
